import { Fragment, useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { cn } from '../../lib/cn';
import { hcFillSelected } from '../../lib/forcedColors';
import { useIsoLayoutEffect } from '../../lib/useIsoLayoutEffect';
import { UNSAVED_SECTION_MESSAGE, UnsavedChangesDialog } from '../ui/UnsavedChangesDialog';
import { FooterSaveProvider } from './FooterSaveProvider';
import { StickySaveFooter } from './StickySaveFooter';
import { useFooterSaveState, type FooterSaveState } from './footerSave';
import { SettingsTabKeyContext } from './settingsTabKey';
import { resolveTab, tabAfterKey, TAB_PARAM } from './settingsTabsModel';
import { useUnsavedChangesGuard } from './useUnsavedChangesGuard';

export interface SettingsTab {
  /** Its `?tab=` value: stable and URL-safe, since links and reloads open a tab by it. */
  key: string;
  /** The tab's text. It may be shorter than the section's title, which stays as it is in the panel. */
  label: string;
  /** False hides the tab, by the same rule that hides its card (role, install). Default true. */
  visible?: boolean;
  /**
   * The sticky Save footer shows on this tab: it has fields THIS person can save. Default false. Its
   * cards register with useFooterSave; on a tab without the footer they keep their own Save.
   */
  footer?: boolean;
  /** The panel's content. Called for the selected tab only, so only its cards mount (and load). */
  render: () => ReactNode;
}

/** What a tab without the footer reads: nothing is ever dirty there. */
const NO_FOOTER: FooterSaveState = {
  dirty: false,
  canSave: false,
  saving: false,
  status: 'clean',
  saveAll: () => Promise.resolve(true),
  discardAll: () => undefined,
  held: false,
  settleHeld: () => Promise.resolve(true),
};

// The list: from `lg` (where the app sidebar appears) a column 13.5rem wide (216px at full size) on the
// left of the panel, pinned under the top bar while the page scrolls (`top-0` pins at the scroll region's
// 1.5rem padding), scrolling on its own when taller than the window less the top bar, that padding twice
// and the sticky footer. Below `lg` it stacks above the panel as a compact card of tabs that wrap into
// rows. Its padding keeps each tab's focus ring inside the box that scrolls, which would clip it.
const LIST =
  'flex flex-wrap gap-1 rounded-xl border border-line bg-card p-1.5 shadow-card lg:sticky lg:top-0 lg:max-h-[calc(100dvh-6.5rem-var(--sticky-save-footer-h,0rem))] lg:w-[13.5rem] lg:shrink-0 lg:flex-col lg:flex-nowrap lg:gap-0.5 lg:overflow-y-auto lg:rounded-none lg:border-0 lg:bg-transparent lg:shadow-none';
const TAB = 'focus-ring rounded-lg px-3 py-1.5 text-left text-sm transition-colors lg:w-full lg:py-2';
// Selected as in the Weekly slots card's schedule list (ScheduleSidebar); in forced colours a system fill.
const SELECTED = cn('bg-tint font-semibold text-tint-fg', hcFillSelected);
const IDLE = 'text-ink-label hover:bg-chip hover:text-ink';

/**
 * A settings page's sections as tabs (spec "App Settings, Billing Admin and Site Integrations"): a
 * vertical tab list on the left and the selected tab's panel beside it, with the sticky Save footer on
 * the tabs that have it. Everything a page needs is in here: the tab list, the panel, the footer and
 * its FooterSaveProvider, the Unsaved changes dialog on a tab switch, and the same dialog (or the
 * browser's prompt) before the page is left (useUnsavedChangesGuard). The page passes its tabs:
 *
 *   <PageHeader ... />
 *   <SettingsTabs label="App Settings sections" tabs={[{ key, label, visible, footer, render }, ...]} />
 *
 * Which tab shows: the one `?tab=` names while it is visible; else `initialTab` while it is visible (for
 * an arrival that means a tab, such as the Canva return's `?canva=`); else the first visible tab. A
 * switch writes `?tab=<key>` with `replace`, so a reload or a link keeps the tab and Back leaves the page
 * rather than stepping through tabs. A card that clears the arrival's parameter writes `?tab=` in its
 * place in that same write (useSettingsTabKey). Only the selected tab's panel is rendered; each switch
 * mounts its cards afresh, so they start from their saved values.
 *
 * A switch away from a tab with unsaved changes asks first (UnsavedChangesDialog, "This section has
 * changes that haven't been saved..."): Keep editing stays, Discard changes puts the saved values back
 * and switches, Save and continue saves and switches, or stays if the save fails.
 *
 * A switch asked for while a save a card runs by its own button is still running (a row form's Save,
 * Remove, Add member: useSaveHold) waits for that save's answer, then goes if it worked and stays if
 * it failed, where the card shows why. The last tab chosen meanwhile is the one opened; choosing the
 * open tab again drops the switch. Leaving the page waits the same way (useUnsavedChangesGuard).
 *
 * Keyboard (WAI-ARIA tabs, MANUAL activation): Up/Down (and Left/Right, for the wrapped narrow list),
 * Home and End move the focus among the tabs; Enter, Space or a click selects one. Not automatic
 * activation, where the focus selects: selecting mounts a tab's cards, several of which ask a service
 * when they mount (the Azure deployment list, the Apify and Figma account lookups), and leaving a tab
 * with changes asks first. Arrowing past a tab must do neither. The list is one Tab stop: the selected
 * tab, or the one the arrows last focused while the focus is in the list.
 *
 * Laid out for StandardLayout's scroll region; see StickySaveFooter for the footer's part of that.
 */
export function SettingsTabs({
  tabs,
  label,
  initialTab,
}: {
  /** Every section in page order, the hidden ones too (`visible: false`). */
  tabs: SettingsTab[];
  /** The tab list's accessible name, e.g. "App Settings sections". */
  label: string;
  /** Opened when `?tab=` names no visible tab. Read once, when the page mounts. */
  initialTab?: string;
}) {
  const [searchParams] = useSearchParams();
  // The person's choice, also kept here so a change elsewhere to the query (the Canva card clearing its
  // `?canva=`) never moves them off the tab they are on.
  const [chosen, setChosen] = useState<string | null>(initialTab ?? null);
  const selectedKey = resolveTab(tabs, searchParams.get(TAB_PARAM), chosen);
  const selected = tabs.find((tab) => tab.key === selectedKey) ?? null;

  return (
    <FooterSaveProvider active={selected?.footer === true} scope={selectedKey ?? ''}>
      <TabsAndPanel tabs={tabs} label={label} selected={selected} onChoose={setChosen} />
    </FooterSaveProvider>
  );
}

function TabsAndPanel({
  tabs,
  label,
  selected,
  onChoose,
}: {
  tabs: SettingsTab[];
  label: string;
  selected: SettingsTab | null;
  onChoose: (key: string) => void;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const footer = useFooterSaveState() ?? NO_FOOTER;
  const base = useId();
  const tabId = (key: string) => `${base}-tab-${key}`;
  const panelId = `${base}-panel`;
  const tabRefs = useRef(new Map<string, HTMLButtonElement>());
  const listRef = useRef<HTMLDivElement>(null);
  // The tab holding the list's Tab stop while the focus is in the list; null puts it on the selected tab.
  const [focusKey, setFocusKey] = useState<string | null>(null);
  // A switch waiting on the Unsaved changes dialog, and the tab that asked (focus goes back to it).
  const [pending, setPending] = useState<{ key: string; opener: HTMLElement | null } | null>(null);

  // The query as it is NOW: a switch made after a save resolves must not write back a query from
  // before it (setSearchParams' own `prev` is the one from the render that made the handler). The
  // footer's state likewise, for a switch that waited on a save.
  const query = useRef(searchParams);
  const latestFooter = useRef(footer);
  useIsoLayoutEffect(() => {
    query.current = searchParams;
    latestFooter.current = footer;
  });
  // A switch that waited on a save must not act once the page is gone.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  // The tab a switch will open once the saves running on this tab settle (useSaveHold); null: none waits.
  const waiting = useRef<string | null>(null);

  const visible = tabs.filter((tab) => tab.visible !== false);
  const keys = visible.map((tab) => tab.key);
  const selectedKey = selected?.key ?? null;
  const tabStop = focusKey !== null && keys.includes(focusKey) ? focusKey : selectedKey;

  const go = (key: string) => {
    onChoose(key);
    setSearchParams(
      () => {
        const next = new URLSearchParams(query.current);
        next.set(TAB_PARAM, key);
        return next;
      },
      { replace: true },
    );
  };

  /** Switches to `key`, asking first when the tab has unsaved changes. */
  const switchTo = (key: string) => {
    if (latestFooter.current.dirty) {
      const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      setPending({ key, opener });
      return;
    }
    go(key);
  };

  const ask = (key: string) => {
    if (key === selectedKey) {
      // The open tab, chosen again: stay, and drop a switch that was waiting.
      waiting.current = null;
      return;
    }
    if (footer.held) {
      // A save this tab started by its own button is still running: switching now would unmount its
      // card, and its answer would be shown nowhere. Wait for it; go only if it worked.
      const alreadyWaiting = waiting.current !== null;
      waiting.current = key;
      if (!alreadyWaiting) {
        void footer.settleHeld().then((ok) => {
          const next = waiting.current;
          waiting.current = null;
          if (ok && next !== null && mounted.current) switchTo(next);
        });
      }
      return;
    }
    switchTo(key);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, key: string) => {
    // Alt+arrows and the like belong to the browser.
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const next = tabAfterKey(keys, key, event.key);
    if (next === undefined) return;
    event.preventDefault();
    setFocusKey(next);
    tabRefs.current.get(next)?.focus();
  };

  // Keeps the selected tab in view when the list scrolls on its own (a short window). Moves the list's
  // own scroll only, never the page's, as scrollIntoView would.
  useEffect(() => {
    const list = listRef.current;
    const tab = selectedKey === null ? undefined : tabRefs.current.get(selectedKey);
    if (!list || !tab || tab.offsetParent !== list || list.scrollHeight <= list.clientHeight) return;
    const top = tab.offsetTop;
    const bottom = top + tab.offsetHeight;
    if (top < list.scrollTop) list.scrollTop = top;
    else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight;
  }, [selectedKey]);

  const guard = useUnsavedChangesGuard(footer.dirty, {
    save: footer.saveAll,
    discard: footer.discardAll,
    canSave: footer.canSave,
    saving: footer.saving,
    message: UNSAVED_SECTION_MESSAGE,
    held: footer.held,
    settleHeld: footer.settleHeld,
  });

  return (
    // The footer's sticky box: it spans the tabs and the panel, so the footer is held to the window's
    // bottom all the way down the page. It grows to the bottom of the content area (StandardLayout's
    // column fills the scroll region on a page that holds it), and the row of tabs and panel grows
    // inside it: on a tab shorter than the window, the footer rests at the bottom of the content area,
    // not under the card. The row's items stay at its top (`lg:items-start`).
    <div data-settings-tabs="" className="flex grow flex-col">
      <div className="flex grow flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
        <div
          ref={listRef}
          role="tablist"
          aria-orientation="vertical"
          aria-label={label}
          className={LIST}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setFocusKey(null);
          }}
        >
          {visible.map((tab) => {
            const isSelected = tab.key === selectedKey;
            return (
              <button
                key={tab.key}
                ref={(el) => {
                  if (el) tabRefs.current.set(tab.key, el);
                  else tabRefs.current.delete(tab.key);
                }}
                type="button"
                role="tab"
                id={tabId(tab.key)}
                aria-selected={isSelected}
                aria-controls={panelId}
                tabIndex={tab.key === tabStop ? 0 : -1}
                onClick={() => ask(tab.key)}
                onFocus={() => setFocusKey(tab.key)}
                onKeyDown={(event) => onKeyDown(event, tab.key)}
                className={cn(TAB, isSelected ? SELECTED : IDLE)}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
        {/* One panel, its content swapped: every tab's aria-controls names a panel that exists. Reachable
            by Tab, for a panel with nothing focusable at its top. It takes the room beside the list from
            `lg` only: below it the row is a column that grows, and a growing panel would stretch its
            focus ring down to the footer. */}
        <div
          role="tabpanel"
          id={panelId}
          aria-labelledby={selectedKey === null ? undefined : tabId(selectedKey)}
          tabIndex={0}
          className="focus-ring min-w-0 space-y-6 rounded-xl lg:flex-1"
        >
          {/* Keyed, so two tabs drawing the same component never share its state. */}
          {selected && (
            <Fragment key={selected.key}>
              <SettingsTabKeyContext.Provider value={selected.key}>{selected.render()}</SettingsTabKeyContext.Provider>
            </Fragment>
          )}
        </div>
      </div>
      {selected?.footer && <StickySaveFooter fullWidth />}
      <UnsavedChangesDialog
        open={pending !== null}
        message={UNSAVED_SECTION_MESSAGE}
        saving={footer.saving}
        canSave={footer.canSave}
        returnFocusTo={pending?.opener}
        onKeepEditing={() => setPending(null)}
        onDiscard={() => {
          if (!pending) return;
          footer.discardAll();
          setPending(null);
          go(pending.key);
        }}
        onSave={async () => {
          if (!pending) return;
          const { key } = pending;
          const ok = await footer.saveAll();
          setPending(null);
          // A failed save stays on this tab, where the card shows why.
          if (ok) go(key);
        }}
      />
      {guard}
    </div>
  );
}
